/**
 * Shared helpers for product variant pricing and QR code label images, so every
 * page that renders or downloads a variant QR produces the exact same output.
 */

/**
 * The API stores MRP in `price` and the discounted selling price in
 * `discount_amount` - but `discount_amount` is 0 when there is no discount.
 * So a discount only counts when it is greater than 0 and below the MRP.
 */
export function priceOf(variant) {
  if (!variant) {
    return { mrp: 0, selling: 0, savings: 0, percent: 0 };
  }

  const mrp = Number(variant.mrp ?? variant.price ?? 0);
  const discounted = Number(variant.discount_amount ?? 0);
  const selling = discounted > 0 && discounted < mrp ? discounted : mrp;
  const savings = Math.max(mrp - selling, 0);

  return {
    mrp,
    selling,
    savings,
    percent: mrp > 0 ? Math.round((savings / mrp) * 100) : 0,
  };
}

function formatAmount(value) {
  return Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

/**
 * Build the composite QR label: product name + variant on top, QR in the
 * middle, then the QR code text, MRP/discount (only when discounted) and the
 * final price at the bottom.
 *
 * @param {Object} options
 * @param {HTMLCanvasElement|HTMLImageElement|string} options.qrSource rendered QR canvas, image or data URL
 * @param {Object} options.variant variant record from the API
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function buildVariantQrLabel({ qrSource, variant }) {
  const qr = typeof qrSource === "string" ? await loadImage(qrSource) : qrSource;

  const productName = variant.product ? variant.product.name : "Product";
  const variantText = `${variant.color || ""}${
    variant.size ? " - " + variant.size : ""
  }`.trim();
  const pricing = priceOf(variant);
  const hasDiscount = pricing.savings > 0;
  const qrCodeText = variant.qr_code_data || "";

  const mrpText = `MRP: ₹${formatAmount(pricing.mrp)}`;
  const discountText = `Discount: ₹${formatAmount(pricing.savings)} (${pricing.percent}% off)`;
  const finalText = `₹${formatAmount(pricing.selling)}`;

  const padding = 24;
  const qrSize = qr.width || 300;
  const topTitleHeight = 36;
  const topSubHeight = 26;
  const qrCodeLineHeight = qrCodeText ? 24 : 0;
  // MRP and discount lines are only printed when there is an actual discount
  const mrpLineHeight = hasDiscount ? 24 : 0;
  const discountLineHeight = hasDiscount ? 24 : 0;
  const finalPriceHeight = 36;
  const gap = 14;

  const canvas = document.createElement("canvas");
  canvas.width = qrSize + padding * 2;
  canvas.height =
    padding +
    topTitleHeight +
    topSubHeight +
    gap +
    qrSize +
    gap +
    qrCodeLineHeight +
    mrpLineHeight +
    discountLineHeight +
    finalPriceHeight +
    padding;

  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const centerX = canvas.width / 2;
  let cursorY = padding;

  // Product name
  ctx.fillStyle = "#000000";
  ctx.font = "bold 22px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(productName, centerX, cursorY);
  cursorY += topTitleHeight;

  // Variant (color - size)
  ctx.fillStyle = "#555555";
  ctx.font = "16px Arial, sans-serif";
  ctx.fillText(variantText, centerX, cursorY);
  cursorY += topSubHeight + gap;

  // QR code
  ctx.drawImage(qr, padding, cursorY, qrSize, qrSize);
  cursorY += qrSize + gap;

  // QR code text (e.g. PV-9-QZWXSQ)
  if (qrCodeText) {
    ctx.fillStyle = "#000000";
    ctx.font = "bold 15px 'Courier New', monospace";
    ctx.fillText(qrCodeText, centerX, cursorY);
    cursorY += qrCodeLineHeight;
  }

  // MRP + discount (only when the variant is actually discounted)
  if (hasDiscount) {
    ctx.fillStyle = "#555555";
    ctx.font = "14px Arial, sans-serif";
    ctx.fillText(mrpText, centerX, cursorY);
    cursorY += mrpLineHeight;

    ctx.fillStyle = "#d32f2f";
    ctx.font = "14px Arial, sans-serif";
    ctx.fillText(discountText, centerX, cursorY);
    cursorY += discountLineHeight;
  }

  // Final price
  ctx.fillStyle = "#1976d2";
  ctx.font = "bold 22px Arial, sans-serif";
  ctx.fillText(finalText, centerX, cursorY + 4);

  return canvas;
}

/**
 * Build the label and trigger a PNG download.
 */
export async function downloadVariantQrLabel({ qrSource, variant }) {
  const canvas = await buildVariantQrLabel({ qrSource, variant });
  const productName = variant.product ? variant.product.name : "Product";
  const namePart = variant.qr_code_data || productName;

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `QR_${namePart}_${variant.color}_${variant.size}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      resolve();
    });
  });
}
